import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { getActiveMap, markMapCompleted } from '@/map/MapData';
import { recordLoss, recordWin, getLossStreak, getDefeatTitle, getRetryLabel, getContextualHint, incrementEasterEggStat } from '@/systems/EasterEggs';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { LevelNode, DEMO_LEVEL_CAP, isDemoCapped } from '@/map/LevelTree';
import { isDevMode } from '@/systems/DevConsole';
import { RobotManager } from '@/systems/RobotManager';
import { SoundManager, MusicTrack } from '@/systems/SoundManager';
import { TIER_COLORS } from '@/config/AchievementDefs';
import { AchievementManager } from '@/systems/AchievementManager';
import { t } from '@/i18n/I18n';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { createPanel, createBanner } from '@/ui/UISwitch';

export interface GameOverContext {
  won: boolean;
  scene: Phaser.Scene;
  robotManager: RobotManager | undefined;
  rollbackToSnapshot: () => void;
}

export class GameOverOverlay {
  constructor(private scene: Phaser.Scene) {}

  setup(): void {}
  destroy(): void {}

  show(ctx: GameOverContext): void {
    const { won, scene } = ctx;

    SoundManager.playMusic(MusicTrack.END_CREDITS);

    // Easter egg: Rage Quit Detector
    const mapId = getActiveMap().id;
    let lossCount = 0;
    if (won) {
      recordWin(mapId);
      if (GOD.eePacifist && ctx.robotManager) {
        // Pacifist = ALL deployed robots have zero weapons (not just one)
        const deployed = ctx.robotManager.robots.filter(r => r.components.length > 0);
        const hasPacifist = deployed.length > 0 && deployed.every(
          r => r.getWeapons().length === 0
        );
        if (hasPacifist) {
          incrementEasterEggStat('pacifistWins');
          // Check immediately so UIScene toast shows during game-over, not delayed to TitleScene
          AchievementManager.checkAll();
          // Consume pending so TitleScene doesn't re-show (UIScene toast already handled it)
          AchievementManager.consumePendingNotifications();
        }
      }
    } else {
      lossCount = GOD.eeRageQuit ? recordLoss(mapId) : 0;
    }

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;

    const missionScore = scene.registry.get('missionScore') as import('@/systems/ScoreManager').MissionScore | undefined;
    const levelNode = scene.registry.get('currentLevelNode') as LevelNode | undefined;
    const isCampaign = !!levelNode;

    const dims = missionScore?.dimensions ?? [];
    const stars = missionScore?.stars ?? 0;
    const isPlatinum = missionScore?.isPlatinum ?? false;
    const totalScore = missionScore?.totalScore ?? 0;
    const maxScore = missionScore?.maxScore ?? 1;

    const medalTier = stars >= 3 ? 'GOLD' : stars >= 2 ? 'SILVER' : stars >= 1 ? 'BRONZE' : 'IRON';
    const medalColor = stars >= 3 ? '#ffcc00' : stars >= 2 ? '#cccccc' : stars >= 1 ? '#cc8844' : '#777788';
    const medalColorHex = stars >= 3 ? 0xffcc00 : stars >= 2 ? 0xcccccc : stars >= 1 ? 0xcc8844 : 0x777788;

    const panelW = 460;
    const rowH = 20;
    const dimCount = dims.length;
    const panelH = 126 + dimCount * rowH + 150 + (won && isCampaign ? 60 : 0);
    const panelX = cx - panelW / 2;
    const panelY = cy - panelH / 2 - 10;

    const overlay = scene.add.rectangle(cx, cy, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0).setDepth(200).setInteractive();

    scene.tweens.add({
      targets: overlay,
      alpha: 0.85,
      duration: 1200,
      ease: 'Quad.easeIn',
      onComplete: () => {
        const d = 201;

        // Sprite UI panel (when enabled) or fallback to procedural graphics
        const spritePanel = createPanel(scene, cx, panelY + panelH / 2, panelW, panelH, 'large', d);
        const panelBg = scene.add.graphics().setDepth(d);
        if (spritePanel) {
          // Sprite panel replaces the Graphics panel — just darken interior
          panelBg.fillStyle(0x000000, 0.3);
          panelBg.fillRoundedRect(panelX, panelY, panelW, panelH, 6);
        } else {
          panelBg.fillStyle(0x111128, 0.95);
          panelBg.fillRoundedRect(panelX, panelY, panelW, panelH, 6);
          panelBg.lineStyle(2, won ? 0x44ff44 : 0xff4444, 0.8);
          panelBg.strokeRoundedRect(panelX, panelY, panelW, panelH, 6);
        }

        const rageTitle = (!won && GOD.eeRageQuit) ? getDefeatTitle(lossCount) : null;
        const title = won ? t('gameOver.victory') : (rageTitle ?? t('gameOver.defeat'));
        const titleColor = won ? '#44ff44' : (lossCount >= 7 ? '#ff6644' : '#ff4444');
        const titleText = scene.add.text(cx, panelY + 20, title, {
          fontSize: lossCount >= 10 ? '18px' : '22px', color: titleColor, fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(d + 1).setAlpha(0);

        const elapsedMs = missionScore?.elapsedMs ?? 0;
        const totalSec = Math.floor(elapsedMs / 1000);
        const mins = Math.floor(totalSec / 60);
        const secs = totalSec % 60;
        const centis = Math.floor((elapsedMs % 1000) / 10);
        const chronoStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(centis).padStart(2, '0')}`;
        const speedDim = dims.find(dd => dd.id === 'speed');
        const speedRatio = speedDim?.ratio ?? 0;
        const chronoColor = speedRatio >= 0.75 ? '#44aaff' : speedRatio >= 0.50 ? '#44ff44' : speedRatio >= 0.25 ? '#ffcc44' : '#ff4444';
        const chronoText = scene.add.text(cx, panelY + 40, chronoStr, {
          fontSize: '13px', color: chronoColor, fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(d + 1).setAlpha(0);

        const divGfx = scene.add.graphics().setDepth(d + 1).setAlpha(0);
        divGfx.lineStyle(1, 0x444466, 0.6);
        divGfx.lineBetween(panelX + 20, panelY + 54, panelX + panelW - 20, panelY + 54);

        const labelStyle = { fontSize: '11px', color: '#8888aa', fontFamily: 'monospace' };
        const scoreStyle = { fontSize: '11px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold' };
        const gradeStyle = { fontSize: '16px', fontFamily: 'monospace', fontStyle: 'bold' };
        const lx = panelX + 22;
        const barX = panelX + 160;
        const barW = 140;
        const barH = 10;
        const ptsX = panelX + 316;
        const gradeX = panelX + panelW - 30;
        let sy = panelY + 66;

        const dimElements: Phaser.GameObjects.GameObject[] = [];
        const gradeStamps: Phaser.GameObjects.Text[] = [];

        for (let i = 0; i < dimCount; i++) {
          const dim = dims[i];

          const lblText = dim.isBonus ? `★ ${dim.label}` : dim.label;
          const lbl = scene.add.text(lx, sy, lblText, {
            ...labelStyle,
            color: dim.isBonus ? '#88aa66' : (labelStyle as { color?: string }).color ?? '#aaaacc',
          }).setDepth(d + 1).setAlpha(0);

          const barBg = scene.add.rectangle(barX, sy + 5, barW, barH, 0x222244)
            .setOrigin(0, 0.5).setDepth(d + 1).setAlpha(0);

          const fillColor = Phaser.Display.Color.HexStringToColor(dim.gradeColor).color;
          const fillW = Math.max(1, barW * dim.ratio);
          const barFill = scene.add.rectangle(barX, sy + 5, fillW, barH, fillColor, 0.8)
            .setOrigin(0, 0.5).setDepth(d + 2).setAlpha(0);

          const ptsStr = dim.detail ?? `${dim.points}/${dim.maxPoints}`;
          const pts = scene.add.text(ptsX, sy, ptsStr, scoreStyle)
            .setDepth(d + 1).setAlpha(0);

          const displayGrade = won ? dim.grade : 'F';
          const displayGradeColor = won ? dim.gradeColor : '#ff4444';
          const grade = scene.add.text(gradeX, sy + 1, displayGrade, {
            ...gradeStyle, color: displayGradeColor,
          }).setOrigin(0.5, 0).setDepth(d + 3).setAlpha(0).setScale(2.5);
          gradeStamps.push(grade);

          dimElements.push(lbl, barBg, barFill, pts);
          sy += rowH;
        }

        const divGfx2 = scene.add.graphics().setDepth(d + 1).setAlpha(0);
        divGfx2.lineStyle(1, 0x444466, 0.6);
        divGfx2.lineBetween(panelX + 20, sy + 4, panelX + panelW - 20, sy + 4);
        sy += 14;

        const overallRatio = maxScore > 0 ? totalScore / maxScore : 0;
        const overallGradeInfo = !won
          ? { grade: 'F', color: '#ff4444' }
          : dims.length > 0
            ? { grade: overallRatio >= 0.95 ? 'S' : overallRatio >= 0.80 ? 'A' : overallRatio >= 0.65 ? 'B' : overallRatio >= 0.50 ? 'C' : overallRatio >= 0.35 ? 'D' : overallRatio >= 0.20 ? 'E' : 'F',
                color: overallRatio >= 0.95 ? '#ffdd00' : overallRatio >= 0.80 ? '#44ff44' : overallRatio >= 0.65 ? '#44aaff' : overallRatio >= 0.50 ? '#cccc44' : overallRatio >= 0.35 ? '#ff8844' : overallRatio >= 0.20 ? '#ff4444' : '#884444' }
            : { grade: 'F', color: '#884444' };

        const totalLabel = scene.add.text(lx, sy, t('waveClear.scoreTotal'), {
          fontSize: '13px', color: '#aaaacc', fontFamily: 'monospace', fontStyle: 'bold',
        }).setDepth(d + 1).setAlpha(0);
        const totalVal = scene.add.text(ptsX, sy, `${totalScore} / ${maxScore}`, {
          fontSize: '13px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
        }).setDepth(d + 1).setAlpha(0);
        const overallGrade = scene.add.text(gradeX, sy, overallGradeInfo.grade, {
          fontSize: '36px', color: overallGradeInfo.color, fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0.5, 0).setDepth(d + 3).setAlpha(0).setScale(4);
        sy += 28;

        const starY = sy + 8;
        const starSpacing = 50;
        const starStart = cx - starSpacing;
        const starObjects: Phaser.GameObjects.Text[] = [];

        for (let i = 0; i < 3; i++) {
          const earned = i < stars;
          const starColor = earned ? '#ffcc00' : '#333355';
          const star = scene.add.text(starStart + i * starSpacing, starY, earned ? '\u2605' : '\u2606', {
            fontSize: '48px', color: starColor, fontFamily: 'monospace',
          }).setOrigin(0.5).setDepth(d + 2).setAlpha(0);
          starObjects.push(star);
        }

        const platStar = scene.add.text(starStart + 3 * starSpacing, starY, '\u2606', {
          fontSize: '48px', color: '#333355', fontFamily: 'monospace',
        }).setOrigin(0.5).setDepth(d + 2).setAlpha(0);
        starObjects.push(platStar);

        const starLabel = scene.add.text(cx, starY + 32, won ? t('waveClear.stars', { n: stars }) : t('waveClear.noStars'), {
          fontSize: '10px', color: '#888899', fontFamily: 'monospace',
        }).setOrigin(0.5).setDepth(d + 1).setAlpha(0);

        // Reward breakdown
        const rewardElements: Phaser.GameObjects.GameObject[] = [];
        const victoryReward = scene.registry.get('victoryReward') as
          import('@/systems/ProgressionManager').VictoryReward | undefined;
        if (won && victoryReward) {
          const rwY = starY + 50;
          const rwStyle = { fontSize: '11px', fontFamily: 'monospace' };
          const rwLabelX = panelX + 80;
          const rwValX = panelX + panelW - 80;

          const rwIcoSz = 14;
          const addCoinVal = (vx: number, vy: number, text: string, color: string, bold = false) => {
            const val = scene.add.text(vx, vy, text, { ...rwStyle, color, fontStyle: bold ? 'bold' : '' })
              .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
            const ico = scene.add.image(vx - val.width - 4, vy, 'icon_coin').setDisplaySize(rwIcoSz, rwIcoSz)
              .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
            rewardElements.push(ico, val);
          };

          const baseLbl = scene.add.text(rwLabelX, rwY, t('waveClear.baseReward'), { ...rwStyle, color: '#8888aa' })
            .setOrigin(0, 0.5).setDepth(d + 1).setAlpha(0);
          rewardElements.push(baseLbl);
          addCoinVal(rwValX, rwY, `+${victoryReward.baseReward}`, '#ffffff');

          if (victoryReward.rankBonus > 0) {
            const tierName = victoryReward.newRankTier >= 4 ? t('waveClear.tierPlatinum')
              : victoryReward.newRankTier >= 3 ? t('waveClear.tierGold')
              : victoryReward.newRankTier >= 2 ? t('waveClear.tierSilver') : t('waveClear.tierRank');
            const tierColor = victoryReward.newRankTier >= 4 ? '#44ccff'
              : victoryReward.newRankTier >= 3 ? '#ffcc00'
              : victoryReward.newRankTier >= 2 ? '#cccccc' : '#cc8844';
            const bonusLbl = scene.add.text(rwLabelX, rwY + 16, t('waveClear.rankBonus', { tier: tierName }), { ...rwStyle, color: tierColor })
              .setOrigin(0, 0.5).setDepth(d + 1).setAlpha(0);
            rewardElements.push(bonusLbl);
            addCoinVal(rwValX, rwY + 16, `+${victoryReward.rankBonus}`, tierColor, true);
          }

          let resOffset = victoryReward.rankBonus > 0 ? 34 : 18;
          if (victoryReward.bonusCo > 0 || victoryReward.bonusElectro > 0) {
            const resY = rwY + resOffset;
            const resLbl = scene.add.text(rwLabelX, resY, t('waveClear.resources'), { ...rwStyle, color: '#88aa88' })
              .setOrigin(0, 0.5).setDepth(d + 1).setAlpha(0);
            rewardElements.push(resLbl);
            let rx = rwValX;
            if (victoryReward.bonusElectro > 0) {
              const eTxt = scene.add.text(rx, resY, `+${victoryReward.bonusElectro}`, { ...rwStyle, color: '#88ccff', fontStyle: 'bold' })
                .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
              rx -= eTxt.width + 4;
              const eIco = scene.add.image(rx, resY, 'icon_electro').setDisplaySize(rwIcoSz, rwIcoSz)
                .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
              rx -= rwIcoSz + 10;
              rewardElements.push(eTxt, eIco);
            }
            if (victoryReward.bonusCo > 0) {
              const cTxt = scene.add.text(rx, resY, `+${victoryReward.bonusCo}`, { ...rwStyle, color: '#44ee88', fontStyle: 'bold' })
                .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
              rx -= cTxt.width + 4;
              const cIco = scene.add.image(rx, resY, 'icon_co').setDisplaySize(rwIcoSz, rwIcoSz)
                .setOrigin(1, 0.5).setDepth(d + 1).setAlpha(0);
              rewardElements.push(cTxt, cIco);
            }
            resOffset += 16;
          }

          const totY = rwY + resOffset;
          const totLbl = scene.add.text(rwLabelX, totY, t('waveClear.total'), { ...rwStyle, color: '#44ff44', fontStyle: 'bold' })
            .setOrigin(0, 0.5).setDepth(d + 1).setAlpha(0);
          rewardElements.push(totLbl);
          addCoinVal(rwValX, totY, `+${victoryReward.totalReward}`, '#44ff44', true);

          const reruns = victoryReward.reruns;
          if (reruns > 0) {
            const rerunNote = scene.add.text(cx, totY + 16, t('waveClear.rerunNote', { n: reruns }), {
              fontSize: '9px', color: '#666688', fontFamily: 'monospace',
            }).setOrigin(0.5).setDepth(d + 1).setAlpha(0);
            rewardElements.push(rerunNote);
          }
        }

        // Buttons
        const btnY = panelY + panelH - 34;
        const makeBtn = (bx: number, label: string, color: number, onClick: () => void) => {
          const bg = scene.add.rectangle(bx, btnY, 160, 36, 0x222244, 0.9)
            .setStrokeStyle(2, color).setDepth(d + 2).setAlpha(0)
            .setInteractive({ useHandCursor: true });
          const txt = scene.add.text(bx, btnY, label, {
            fontSize: '13px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(d + 3).setAlpha(0);
          bg.on('pointerover', () => { bg.setFillStyle(0x334466, 1); txt.setColor('#ffcc00'); });
          bg.on('pointerout', () => { bg.setFillStyle(0x222244, 0.9); txt.setColor('#ffffff'); });
          bg.on('pointerdown', onClick);
          return [bg, txt];
        };

        // Demo completion: show congratulations when winning the last demo level
        const isDemoComplete = won && isCampaign && levelNode!.index >= DEMO_LEVEL_CAP && !isDevMode();

        if (isDemoComplete) {
          // ── Congratulation banner ──
          const congratY = btnY - 60;
          const congratTitle = scene.add.text(cx, congratY - 18, '🎉  DEMO COMPLETE!  🎉', {
            fontSize: '16px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(d + 3).setAlpha(0);
          const congratMsg = scene.add.text(cx, congratY + 6, 'Thank you for playing the demo!\nThe full game is coming soon.', {
            fontSize: '11px', color: '#aaccff', fontFamily: 'monospace',
            align: 'center', lineSpacing: 4,
          }).setOrigin(0.5).setDepth(d + 3).setAlpha(0);
          scene.tweens.add({ targets: [congratTitle, congratMsg], alpha: 1, duration: 800, delay: 1500 });
          // Pulsing glow on the title
          scene.tweens.add({
            targets: congratTitle, scaleX: 1.05, scaleY: 1.05,
            yoyo: true, repeat: -1, duration: 800, ease: 'Sine.easeInOut',
          });
        }

        const acceptLabel = isDemoComplete
          ? 'BACK TO MENU'
          : (isCampaign ? t('gameOver.nextLevel') : (won ? t('gameOver.nextLevel') : t('gameOver.backToTitle')));
        const acceptBtn = makeBtn(cx + 95, acceptLabel, isDemoComplete ? 0x6688cc : 0x44aa44, () => {
          SoundManager.play('clickConfirm');
          if (!won) ctx.rollbackToSnapshot();
          scene.scene.stop('GameScene');
          if (isCampaign) {
            scene.scene.start('LevelTreeScene');
          } else {
            if (won) markMapCompleted(getActiveMap().id, stars);
            const isCustomMap = !!CustomMapStore.getById(getActiveMap().id);
            scene.scene.start('MapSelectScene', { tab: isCustomMap ? 'CUSTOM' : 'OFFICIAL' });
          }
          scene.scene.stop('UIScene');
        });

        const rageRetryLabel = (!won && GOD.eeRageQuit) ? getRetryLabel(lossCount) : null;
        const retryLabel = rageRetryLabel ?? t('gameOver.retry');
        const retryColor = lossCount >= 10 ? 0xff4444 : (won ? 0x886644 : 0xcc6644);
        const retryBtn = makeBtn(cx - 95, retryLabel, retryColor, () => {
          SoundManager.play('click');
          // On victory: level is already validated with rewards — just replay
          // On defeat: rollback to undo any mid-game state changes
          if (!won) ctx.rollbackToSnapshot();
          scene.scene.stop('GameScene');
          scene.scene.stop('UIScene');
          scene.scene.start('GameScene');
          scene.scene.start('UIScene');
        });

        if (!won && GOD.eeRageQuit && lossCount >= 15) {
          const hint = getContextualHint({});
          const hintY2 = panelY + panelH - 60;
          const hintText = scene.add.text(cx, hintY2, hint, {
            fontSize: '10px', color: '#88ccff', fontFamily: 'monospace', fontStyle: 'italic',
          }).setOrigin(0.5).setDepth(d + 2).setAlpha(0);
          scene.tweens.add({ targets: hintText, alpha: 1, duration: 800, delay: 2000 });
        }

        // ANIMATION SEQUENCE
        scene.tweens.add({ targets: [titleText, chronoText, divGfx], alpha: 1, duration: 400, ease: 'Quad.easeOut' });

        dimElements.forEach((el, i) => {
          scene.tweens.add({ targets: el, alpha: 1, duration: 250, delay: 300 + Math.floor(i / 4) * 80, ease: 'Quad.easeOut' });
        });

        const stampStartDelay = 300 + dimCount * 80 + 200;
        gradeStamps.forEach((stamp, i) => {
          const stampDelay = stampStartDelay + i * 100;
          scene.time.delayedCall(stampDelay, () => {
            SoundManager.play('stamp');
            scene.tweens.add({
              targets: stamp,
              alpha: 1, scaleX: 1, scaleY: 1,
              duration: 200,
              ease: 'Back.easeOut',
              onComplete: () => {
                scene.cameras.main.shake(60, 0.004);
                if (stamp.text === 'S') {
                  scene.tweens.add({
                    targets: stamp, alpha: 0.7,
                    duration: 500, yoyo: true, repeat: -1,
                    ease: 'Sine.easeInOut',
                  });
                }
              },
            });
          });
        });

        const totalDelay = stampStartDelay + dimCount * 100 + 300;
        scene.tweens.add({ targets: [divGfx2, totalLabel, totalVal], alpha: 1, duration: 300, delay: totalDelay });
        scene.time.delayedCall(totalDelay + 200, () => {
          SoundManager.play('stamp');
          scene.tweens.add({
            targets: overallGrade,
            alpha: 1, scaleX: 1, scaleY: 1,
            duration: 250,
            ease: 'Back.easeOut',
            onComplete: () => {
              scene.cameras.main.shake(120, 0.008);
              if (overallGradeInfo.grade === 'S') {
                scene.tweens.add({
                  targets: overallGrade, alpha: 0.7,
                  duration: 500, yoyo: true, repeat: -1,
                  ease: 'Sine.easeInOut',
                });
              }
            },
          });
        });

        // Sparkle helper
        const spawnSparkles = (sx: number, sy2: number, color: number, count: number, radius: number) => {
          for (let j = 0; j < count; j++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = radius * (0.4 + Math.random() * 0.6);
            const size = 1 + Math.random() * 2.5;
            const spark = scene.add.circle(sx, sy2, size, color, 1).setDepth(d + 4);
            scene.tweens.add({
              targets: spark,
              x: sx + Math.cos(angle) * dist,
              y: sy2 + Math.sin(angle) * dist,
              alpha: 0, scaleX: 0.2, scaleY: 0.2,
              duration: 400 + Math.random() * 400,
              ease: 'Quad.easeOut',
              onComplete: () => spark.destroy(),
            });
          }
        };

        const startStarShimmer = (star: Phaser.GameObjects.Text) => {
          const shimmerLoop = () => {
            if (!star.active) return;
            const ox = star.x - 12 + Math.random() * 24;
            const oy = star.y - 12 + Math.random() * 24;
            const dot = scene.add.circle(ox, oy, 1 + Math.random(), 0xffee88, 0.9).setDepth(d + 4);
            scene.tweens.add({
              targets: dot, alpha: 0, y: oy - 8 - Math.random() * 10,
              duration: 500 + Math.random() * 400, ease: 'Quad.easeOut',
              onComplete: () => dot.destroy(),
            });
          };
          scene.time.addEvent({ delay: 250, callback: shimmerLoop, loop: true });
        };

        const starsDelay = totalDelay + 500;
        for (let i = 0; i < 3; i++) {
          const star = starObjects[i];
          const starDelay = starsDelay + i * 300;
          scene.tweens.add({
            targets: star, alpha: 1, duration: 200, delay: starDelay, ease: 'Quad.easeOut',
            onStart: () => { if (i < stars) SoundManager.play('click'); },
            onComplete: () => {
              if (i < stars) {
                scene.tweens.add({ targets: star, scaleX: 1.5, scaleY: 1.5, duration: 120, yoyo: true, ease: 'Back.easeOut' });
                spawnSparkles(star.x, star.y, 0xffdd44, 10, 30);
                if (stars === 3) startStarShimmer(star);
              }
            },
          });
        }
        const starLabelDelay = starsDelay + 3 * 300 + 100;
        scene.tweens.add({ targets: starLabel, alpha: 1, duration: 300, delay: starLabelDelay });

        // Medal
        const medalSize = Math.round(80 * 1.3);
        const medalR = medalSize / 2;
        const medalX = panelX + panelW - 55 + 50;
        const medalMY = btnY - 50;
        const medalAngle = Math.round(Math.random() * 60 - 30);

        const addMedalShine = (mx: number, my: number, radius: number, _color: number, depth: number) => {
          const shineW = 12;
          const shineH = radius * 2 + 10;
          const shine = scene.add.graphics().setDepth(depth);
          shine.fillStyle(0xffffff, 0.3);
          shine.beginPath();
          shine.moveTo(-shineW / 2, -shineH / 2);
          shine.lineTo(shineW / 2, -shineH / 2);
          shine.lineTo(-shineW / 2 + 8, shineH / 2);
          shine.lineTo(-shineW / 2 - shineW + 8, shineH / 2);
          shine.closePath();
          shine.fillPath();
          shine.fillStyle(0xffffff, 0.18);
          shine.beginPath();
          shine.moveTo(-3, -shineH / 2);
          shine.lineTo(3, -shineH / 2);
          shine.lineTo(-3 + 8, shineH / 2);
          shine.lineTo(-3 - 6 + 8, shineH / 2);
          shine.closePath();
          shine.fillPath();
          shine.setPosition(mx - radius - shineW, my);
          const mask = scene.make.graphics({ x: 0, y: 0 });
          mask.fillCircle(mx, my, radius);
          shine.setMask(mask.createGeometryMask());
          const doSweep = () => {
            shine.setPosition(mx - radius - shineW, my);
            shine.setAlpha(1);
            scene.tweens.add({
              targets: shine, x: mx + radius + shineW,
              duration: 700, ease: 'Sine.easeInOut',
              onComplete: () => shine.setAlpha(0),
            });
          };
          scene.time.delayedCall(600, () => {
            doSweep();
            scene.time.addEvent({ delay: 3500, callback: doSweep, loop: true });
          });
        };

        const spawnConfetti = (count: number, colors: number[]) => {
          for (let i = 0; i < count; i++) {
            const confX = panelX + Math.random() * panelW;
            const confY2 = panelY - 10;
            const color = colors[Math.floor(Math.random() * colors.length)];
            const w = 3 + Math.random() * 4;
            const h = 2 + Math.random() * 3;
            const piece = scene.add.rectangle(confX, confY2, w, h, color, 0.9)
              .setDepth(d + 12).setAngle(Math.random() * 360);
            const targetX = panelX + Math.random() * panelW;
            const targetY2 = panelY + panelH * (0.4 + Math.random() * 0.6);
            scene.tweens.add({
              targets: piece,
              x: targetX, y: targetY2,
              angle: piece.angle + 180 + Math.random() * 360,
              alpha: 0,
              duration: 1500 + Math.random() * 1500,
              delay: Math.random() * 600,
              ease: 'Quad.easeOut',
              onComplete: () => piece.destroy(),
            });
          }
        };

        if (won) {
          const medalDelay = starLabelDelay + 300;
          const medalGfx = scene.add.graphics().setPosition(medalX, medalMY)
            .setDepth(d + 5).setAlpha(0).setScale(4).setAngle(medalAngle);
          const mr = (medalColorHex >> 16) & 0xff;
          const mg = (medalColorHex >> 8) & 0xff;
          const mb = medalColorHex & 0xff;
          const darkerMedal = ((Math.max(0, mr - 80) << 16) | (Math.max(0, mg - 80) << 8) | Math.max(0, mb - 80));
          medalGfx.fillStyle(darkerMedal, 1);
          medalGfx.fillCircle(0, 0, medalR);
          medalGfx.fillStyle(medalColorHex, 0.35);
          medalGfx.fillCircle(0, 0, medalR - 3);
          medalGfx.lineStyle(3, medalColorHex, 1);
          medalGfx.strokeCircle(0, 0, medalR);
          medalGfx.lineStyle(1, 0xffffff, 0.25);
          medalGfx.beginPath();
          medalGfx.arc(0, 0, medalR - 5, -Math.PI * 0.8, -Math.PI * 0.2);
          medalGfx.strokePath();

          const medalSymbol = scene.add.text(medalX, medalMY, medalTier, {
            fontSize: '16px', color: medalColor, fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(d + 6).setAlpha(0).setScale(4).setAngle(medalAngle);

          scene.time.delayedCall(medalDelay, () => {
            scene.tweens.add({
              targets: [medalGfx, medalSymbol], alpha: 1, scaleX: 1, scaleY: 1,
              duration: 200, ease: 'Back.easeOut',
              onComplete: () => {
                scene.cameras.main.shake(200, 0.012);
                SoundManager.play('medalReveal');
                spawnSparkles(medalX, medalMY, medalColorHex, 14, medalR * 1.2);
                addMedalShine(medalX, medalMY, medalR, medalColorHex, d + 6);

                if (stars >= 3 && !isPlatinum) {
                  spawnConfetti(40, [0xffcc00, 0xffee66, 0xddaa00, 0xffffff, 0xff8844]);
                }

                if (isPlatinum) {
                  scene.time.delayedCall(800, () => {
                    SoundManager.play('platinumReveal');
                    const platColor = 0x44ccff;
                    const platStr = '#44ccff';
                    const flash = scene.add.rectangle(cx, cy, CANVAS_WIDTH, CANVAS_HEIGHT, 0xffffff, 0).setDepth(d + 10);
                    scene.tweens.add({ targets: flash, alpha: 0.4, duration: 80, yoyo: true, onComplete: () => flash.destroy() });

                    const platGfx = scene.add.graphics().setPosition(medalX, medalMY)
                      .setDepth(d + 7).setAlpha(0).setScale(5).setAngle(medalAngle);
                    platGfx.fillStyle(0x0a2233, 1);
                    platGfx.fillCircle(0, 0, medalR);
                    platGfx.fillStyle(platColor, 0.3);
                    platGfx.fillCircle(0, 0, medalR - 3);
                    platGfx.lineStyle(3, platColor, 1);
                    platGfx.strokeCircle(0, 0, medalR);
                    platGfx.lineStyle(1, 0xffffff, 0.3);
                    platGfx.beginPath();
                    platGfx.arc(0, 0, medalR - 5, -Math.PI * 0.8, -Math.PI * 0.2);
                    platGfx.strokePath();

                    const platSym = scene.add.text(medalX, medalMY, 'PLATINUM', {
                      fontSize: '12px', color: platStr, fontFamily: 'monospace', fontStyle: 'bold',
                    }).setOrigin(0.5).setDepth(d + 8).setAlpha(0).setScale(5).setAngle(medalAngle);

                    scene.tweens.add({
                      targets: [platGfx, platSym], alpha: 1, scaleX: 1, scaleY: 1,
                      duration: 250, ease: 'Back.easeOut',
                      onComplete: () => {
                        medalGfx.setAlpha(0); medalSymbol.setAlpha(0);
                        scene.cameras.main.shake(300, 0.02);
                        SoundManager.play('clickConfirm');
                        spawnSparkles(medalX, medalMY, platColor, 20, medalR * 1.4);
                        addMedalShine(medalX, medalMY, medalR, platColor, d + 8);

                        spawnConfetti(60, [0x44ccff, 0x88eeff, 0xffffff, 0xffcc00, 0x22aadd]);

                        scene.time.addEvent({ delay: 300, loop: true, callback: () => {
                          if (!platGfx.active) return;
                          const a = Math.random() * Math.PI * 2;
                          const r2 = medalR * (0.3 + Math.random() * 0.7);
                          const px = medalX + Math.cos(a) * r2;
                          const py = medalMY + Math.sin(a) * r2;
                          const sp = scene.add.circle(px, py, 1 + Math.random(), platColor, 0.8).setDepth(d + 9);
                          scene.tweens.add({ targets: sp, alpha: 0, y: py - 6, duration: 500, onComplete: () => sp.destroy() });
                        }});

                        const platStarObj = starObjects[3];
                        platStarObj.setColor(platStr); platStarObj.setText('\u2605');
                        scene.tweens.add({ targets: platStarObj, alpha: 1, duration: 200, onComplete: () => {
                          SoundManager.play('click');
                          scene.tweens.add({ targets: platStarObj, scaleX: 1.8, scaleY: 1.8, duration: 150, yoyo: true, ease: 'Back.easeOut' });
                          spawnSparkles(platStarObj.x, platStarObj.y, platColor, 12, 30);
                          scene.time.addEvent({ delay: 200, loop: true, callback: () => {
                            if (!platStarObj.active) return;
                            const ox = platStarObj.x - 12 + Math.random() * 24;
                            const oy = platStarObj.y - 12 + Math.random() * 24;
                            const dot2 = scene.add.circle(ox, oy, 1 + Math.random(), platColor, 0.9).setDepth(d + 4);
                            scene.tweens.add({ targets: dot2, alpha: 0, y: oy - 8, duration: 500, onComplete: () => dot2.destroy() });
                          }});
                        }});
                        starLabel.setText('4 / 4 Stars — PERFECT'); starLabel.setColor(platStr);
                      },
                    });
                  });
                }
              },
            });
          });
        }

        // Reward breakdown fade in
        if (rewardElements.length > 0) {
          const rewardDelay = starLabelDelay + 300;
          rewardElements.forEach((el, i) => {
            scene.tweens.add({ targets: el, alpha: 1, duration: 250, delay: rewardDelay + i * 60, ease: 'Quad.easeOut' });
          });
        }

        // Buttons
        const rewardExtraDelay = rewardElements.length > 0 ? rewardElements.length * 60 + 400 : 0;
        const btnDelay2 = won ? (isPlatinum ? starLabelDelay + 2000 + rewardExtraDelay : starLabelDelay + 700 + rewardExtraDelay) : totalDelay + 400;
        [...acceptBtn, ...retryBtn].forEach(el => {
          scene.tweens.add({ targets: el, alpha: 1, duration: 300, delay: btnDelay2, ease: 'Quad.easeOut' });
        });
      },
    });
  }
}
